import ReadMore from "@components/ArticleMeta/ReadMore";
import SocialMediaIcons from "@components/ArticleMeta/SocialMediaIcons";
import Card from "@particles/card/Card";
import { BasicCardProps, CardVariations } from "@particles/card/utils/types";
import { ENVIRONMENT } from "@util/constant";
import { cn, getEnvironmentDomain } from "@util/helpers";

type LifestyleCardProps = {
  /** The props needed for the card. */
  cardProps: BasicCardProps;
  /** Optional className for the card. */
  className?: string;
};

/**
 * The card to be used for displaying lifestyle layout. It has a different design from the default card.
 */
export default function LifestyleCard({
  className,
  cardProps,
}: LifestyleCardProps): React.ReactElement {
  const variations: CardVariations = {
    title: {
      color: "text-gray-850 hover:text-lifestyle-primary",
      size: "text-6xl lg:text-8xl",
      extraClass: "font-pt-serif",
    },
    kicker: {
      color: "text-gray-515",
      extraClass: "font-pt-sans tracking-[2px]",
    },
    image: {
      position: "",
      width: "w-full",
      aspectRatio: "aspect-3x2",
      imageClassName: "object-cover",
      orientation: "landscape",
      filters: { w: 960, q: 100, f: "webp" },
    },
    content: {
      width: "w-full",
      extraClass: "pt-4",
      blurb: {
        extraClass: "text-xs w-fit hidden lg:block word-break",
        wrapperClassName: "mt-2",
      },
    },
    subscriberLabel: { extraClass: "mt-2" },
  };

  return (
    <div
      className={cn(
        "justify-self group relative border border-translucent px-4 pb-8 pt-4 hover:border-lifestyle-primary lg:h-full",
        {
          "pt-10":
            typeof cardProps.thumbnail === "undefined" &&
            typeof cardProps.defaultImage === "undefined",
        },
        className
      )}
      data-testid="lifestyle-card-component"
    >
      <Card {...cardProps} variations={variations} className="-mt-10" />

      <div className="absolute top-full mt-0 flex w-full -translate-y-1/2 items-center">
        <div className="flex-1">
          <ReadMore
            to={cardProps.link}
            className="w-auto bg-white py-1.5 font-pt-sans tracking-normal hover:border-x-0"
            isAnimated
          />
        </div>

        <SocialMediaIcons
          className="flex-none"
          shareUrl={new URL(
            cardProps.link,
            getEnvironmentDomain(ENVIRONMENT)
          ).toString()}
        />
      </div>
    </div>
  );
}
